import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import {
  deleteDocumentSupportingDocument,
  DocumentByIdDTO,
  DocumentsDTO,
  DocumentsQuery,
  getDocumentBills,
  getDocumentById,
  getDocuments,
  uploadDocumentSupportingDocument,
} from "api";

import { QUERY_KEYS } from "consts";
import { useNotify } from "hooks";

import { useState } from "react";
import { OptionalRefetchIntervalAndRetry } from "./types";

export const useDocuments: any = ({
  retry = false,
  refetchInterval,
  ...query
}: DocumentsQuery & OptionalRefetchIntervalAndRetry) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoading, setIsLoading] = useState(true);
  const { data, error, refetch } = useQuery<DocumentsDTO>(
    [QUERY_KEYS.DOCUMENTS, query],
    async () => {
      setIsLoading(true);
      try {
        const result = await getDocuments(query);
        setIsLoading(false);
        return result;
        // eslint-disable-next-line @typescript-eslint/no-shadow
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    {
      retry,
      refetchInterval,
      onSuccess: () => setIsLoading(false),
      onError: () => setIsLoading(false),
    },
  );
  return { data, isLoading, error, refetch };
};

export const useDocumentById = (id: string | undefined) =>
  useQuery<DocumentByIdDTO>(
    [QUERY_KEYS.DOCUMENTS, id],
    () => getDocumentById(id!),
    { enabled: !!id },
  );

export const useDocumentBills = (id: string) =>
  useQuery<any>([QUERY_KEYS.DOCUMENT_BILLS, id], () => getDocumentBills(id));

export const useUploadDocumentSupportingDocument = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation(
    ({ guid, file }: { guid: string; file: File }) =>
      uploadDocumentSupportingDocument(guid, file),
    {
      onSuccess: () => {
        notify.success('Document uploaded successfully!');
        queryClient.invalidateQueries([QUERY_KEYS.DOCUMENTS]);
      },
    },
  );
};

export const useDeleteDocumentSupportingDocument = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation(
    (id: number) => deleteDocumentSupportingDocument(id),
    {
      onSuccess: () => {
        notify.success('Document deleted successfully!');
        queryClient.invalidateQueries([QUERY_KEYS.DOCUMENTS]);
      },
      onError: (error: AxiosError) => {
        notify.error(
          typeof error?.response?.data === 'string'
            ? error.response.data
            : 'Failed to delete document',
        );
      },
    },
  );
};
